import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { staffBySlug, staffImg } from '../../data/aiStaff';

/**
 * Coded replicas of the AuraOS client portal for /auraos (handoff
 * 2026-10-12). They copy the app's real components, labels, and messages
 * exactly (quoted app copy keeps its own punctuation) for a fictional
 * business, Harbor & Pine Coffee Co. (owner: Jordan). Each view takes plain
 * props so the "Watch it work" scene can drive the same UI.
 */

export const CANVAS_W = 1100;

// ── Shared app pieces ──────────────────────────────────────────────────

export const HelpIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7M12 17h.01" /></svg>
);
export const ChevronRight = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
);

export const Keycap = () => (
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect x="8" y="9" width="48" height="48" rx="14" fill="#e9e7f0" stroke="#141414" strokeWidth="3" />
    <rect x="8" y="9" width="48" height="42" rx="14" fill="#fff" stroke="#141414" strokeWidth="3" />
    <rect x="14" y="15" width="36" height="30" rx="8" fill="none" stroke="#141414" strokeWidth="1.4" opacity="0.28" />
    <text x="32" y="30.5" fontFamily='"Hanken Grotesk", system-ui, sans-serif' fontSize="19" fontWeight="700" fill="#141414" textAnchor="middle" dominantBaseline="central" letterSpacing="-1.2">BC</text>
  </svg>
);

const TONE_SOFT: Record<string, string> = {
  blue: '#e8f0fe', pink: '#fce8f2', violet: '#f3edfe', orange: '#fdeee4',
  teal: '#e2f7f4', green: '#e7f6ec', amber: '#fdf3dc', slate: '#f1f3f6',
};

export const Avatar = ({ slug, size = 28 }: { slug: string; size?: number }) => {
  const s = staffBySlug(slug);
  return (
    <span className="a-avatar" style={{ width: size, height: size, background: TONE_SOFT[s.tone] }}>
      <img src={staffImg(s.image, 96)} alt="" width={size} height={size} loading="lazy" decoding="async" draggable={false} />
    </span>
  );
};

export const StaffByline = ({ slug, size = 28 }: { slug: string; size?: number }) => {
  const s = staffBySlug(slug);
  return (
    <div className="a-byline">
      <Avatar slug={slug} size={size} />
      <span><b>{s.name}</b> · {s.role}</span>
    </div>
  );
};

export const StaffTeam = ({ slugs, size = 32 }: { slugs: string[]; size?: number }) => {
  const names = slugs.map((s) => staffBySlug(s).name);
  const label =
    names.length >= 4
      ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`
      : names.length > 1
        ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
        : names[0];
  return (
    <div className="a-team">
      <span className="a-team-faces">
        {slugs.map((s) => <Avatar key={s} slug={s} size={size} />)}
      </span>
      <span className="a-team-names">{label}</span>
    </div>
  );
};

/** Browser frame + portal top bar around a portal page body. */
export const PortalChrome = ({
  children,
  menuOpen = false,
  menuHot,
}: {
  children: ReactNode;
  menuOpen?: boolean;
  menuHot?: string;
}) => (
  <div className="aos-frame">
    <div className="aos-frame-bar">
      <span className="aos-frame-dots"><i /><i /><i /></span>
      <span className="aos-frame-url">app.brandctl.com/portal</span>
      <span style={{ width: 42 }} />
    </div>
    <div className="a-top">
      <div className="a-brand">
        <Keycap />
        <span className="a-wordmark"><b>BRANDCENTRAL</b><span>AuraOS</span></span>
      </div>
      <div className="a-top-right">
        <span className="a-btn-ghost a-help"><HelpIcon />Help</span>
        <span className="a-profile pill" data-t="profile"><span className="a-initial">J</span>Jordan<ChevronRight /></span>
      </div>
      {menuOpen && (
        <div className="a-menu">
          {['Team', 'Library', 'Settings', 'Sign out'].map((m) => (
            <div key={m} className={menuHot === m ? 'hot' : undefined} data-t={m === 'Library' ? 'menu-library' : undefined}>{m}</div>
          ))}
        </div>
      )}
    </div>
    <div className="a-body">{children}</div>
  </div>
);

/**
 * A fixed 1100px-wide replica scaled down to fit, so on phones it reads as a
 * shrunken screenshot instead of a reflowed layout. It's labelled as a
 * picture of the product and its controls are inert.
 */
export const ScaledCanvas = ({
  label,
  children,
  overlay,
  innerRef,
  onScale,
  width = CANVAS_W,
}: {
  label: string;
  children: ReactNode;
  overlay?: ReactNode;
  innerRef?: React.RefObject<HTMLDivElement | null>;
  onScale?: (s: number) => void;
  /** Unscaled canvas width in px (default 1100). The hero uses a narrower one so the shot stays legible. */
  width?: number;
}) => {
  const outer = useRef<HTMLDivElement>(null);
  const ownInner = useRef<HTMLDivElement>(null);
  const inner = innerRef ?? ownInner;
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const o = outer.current;
    const i = inner.current;
    if (!o || !i) return;
    const update = () => {
      const s = Math.min(1, o.clientWidth / width);
      setScale(s);
      setHeight(i.offsetHeight * s);
      onScale?.(s);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(o);
    ro.observe(i);
    return () => ro.disconnect();
  }, [inner, onScale, width]);

  return (
    <div ref={outer} className="aos-scaled" style={{ height }}>
      <div role="img" aria-label={label}>
        <div ref={inner} className="aos-scaled-inner aura-app" style={{ width, transform: `scale(${scale})` }} inert>
          {children}
          {overlay}
        </div>
      </div>
    </div>
  );
};

// ── 4.1 Approvals ──────────────────────────────────────────────────────

export type ItemState = 'idle' | 'like' | 'submitting' | 'approved';

const Chevron = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M4.5 3l3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const ItemActions = ({
  state = 'idle',
  feedback = '',
  typing = false,
  likeHot = false,
}: {
  state?: ItemState;
  feedback?: string;
  typing?: boolean;
  likeHot?: boolean;
}) => {
  if (state === 'approved') {
    return (
      <div className="a-approved">
        <b>Approved — it&rsquo;s yours to use.</b>
        <span>Saved for your records. Thanks for the notes — the agent will keep doing this.</span>
      </div>
    );
  }
  return (
    <>
      <p className="a-ask">Before you download — how&rsquo;s this?</p>
      <div className="a-vote">
        <button type="button" className={`like${likeHot || state !== 'idle' ? ' hot' : ''}`} data-t="like">✓ Like it</button>
        <button type="button" className="bad">✕ Needs work</button>
      </div>
      {state !== 'idle' && (
        <div className="a-feedback">
          <label>Great — what did you like about it? (the more detail, the better it gets)</label>
          <div className={`a-textarea${typing ? ' focus' : ''}`} data-t="textarea">
            {feedback}
            {typing && <span className="caret" />}
          </div>
          <div className="a-feedback-actions">
            <button type="button" className="a-btn-primary" data-t="submit">{state === 'submitting' ? 'Submitting…' : 'Submit & download'}</button>
            <button type="button" className="a-btn-ghost">Back</button>
          </div>
        </div>
      )}
    </>
  );
};

export const MayaCard = () => (
  <div className="a-card a-item">
    <StaffByline slug="email-newsletter" size={28} />
    <div className="a-item-meta">
      <span className="a-pill a-pill-kind">Newsletter</span>
      <time>2h ago</time>
    </div>
    <h4>October newsletter: the fall menu is here</h4>
    <p className="ex">Pumpkin cold brew, maple oat latte and our new cardamom bun land this Friday. Plus: the first 50 orders on Saturday get a free pastry.</p>
    <span className="a-preview">Preview the full piece <Chevron /></span>
    <ItemActions />
  </div>
);

export const DiegoBundle = ({
  state = 'idle',
  feedback = '',
  typing = false,
  likeHot = false,
  enter = false,
}: {
  state?: ItemState;
  feedback?: string;
  typing?: boolean;
  likeHot?: boolean;
  enter?: boolean;
}) => (
  <div className={`a-bundle${enter ? ' a-slide-in' : ''}`}>
    <p className="a-bundle-label">Diego (Social Media Manager) · 3 × Instagram post</p>
    <div className="a-card a-item">
      <StaffByline slug="social-media" size={28} />
      <div className="a-item-meta">
        <span className="a-pill a-pill-kind">Instagram post</span>
        <time>just now</time>
      </div>
      <h4>Fall menu drop</h4>
      <p className="ex">New season, new favorites. Pumpkin cold brew is back Friday. Which one are you trying first?</p>
      <span className="a-preview">Preview the full piece <Chevron /></span>
      <ItemActions state={state} feedback={feedback} typing={typing} likeHot={likeHot} />
    </div>
  </div>
);

export const ApprovalsView = ({
  waiting = 2,
  diego,
  diegoFirst = false,
}: {
  waiting?: number;
  diego?: ReactNode | null;
  diegoFirst?: boolean;
}) => {
  const diegoNode = diego === undefined ? <DiegoBundle /> : diego;
  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <h3 className="a-h">Your approvals <span>· {waiting} waiting</span></h3>
      <div className="a-card a-approvals">
        {diegoFirst && diegoNode}
        <MayaCard />
        {!diegoFirst && diegoNode}
      </div>
    </div>
  );
};

// ── 4.2 Your AI Staff (welcome) ────────────────────────────────────────

const WELCOME_TEAM = ['marketing-content', 'sell-by-chat', 'email-newsletter', 'social-media', 'scheduling-appointments'];

export const WelcomeView = () => (
  <>
    <div className="a-dim">
      <ApprovalsView />
      <div className="a-dialog">
        <h4>Welcome to your portal</h4>
        <div className="a-dialog-team">
          <p className="a-caps">Meet your AI Staff</p>
          <StaffTeam slugs={WELCOME_TEAM} size={32} />
        </div>
        <p className="body">Want a quick tour? We&rsquo;ll show you the 4 places you&rsquo;ll use. It takes about a minute, and you can skip at any point.</p>
        <div className="a-dialog-actions">
          <span className="a-btn-ghost">Skip</span>
          <span className="a-btn-primary">Show me around</span>
        </div>
      </div>
    </div>
    <div className="a-welcome-cards">
      {WELCOME_TEAM.map((slug) => (
        <div key={slug} className="a-card a-welcome-card">
          <StaffByline slug={slug} size={40} />
          <p>{staffBySlug(slug).blurb}</p>
        </div>
      ))}
    </div>
  </>
);

// ── 4.3 Sell by Chat ───────────────────────────────────────────────────

export const CHAT = [
  { who: 'cust', text: 'Hi! Do you do catering for office meetings?', time: '9:02' },
  { who: 'ai', text: 'We do! Most offices order our coffee and pastry box for 10 to 40 people. How many are you expecting, and what day?', time: '9:02' },
  { who: 'cust', text: 'About 25, next Thursday morning.', time: '9:04' },
  { who: 'ai', text: "Perfect, that's our Team Box. Want a quick call with our catering lead to lock it in? Pick a time here: harborpine.co/catering", time: '9:04' },
  { who: 'cust', text: 'Booked for Tuesday at 10!', time: '9:07' },
  { who: 'ai', text: "You're all set. See you Tuesday at 10:00, Taylor!", time: '9:07' },
] as const;

const SKY = { dot: '#0ea5e9', soft: '#e0f2fe', ink: '#0369a1' };
const VIOLET = { dot: '#8b5cf6', soft: '#ede9fe', ink: '#6d28d9' };

const Funnel = ({ name, c }: { name: string; c: typeof SKY }) => (
  <span className="a-pill a-funnel" style={{ background: c.soft, color: c.ink }}><i style={{ background: c.dot }} />{name}</span>
);

const Msg = ({ who, text, time, rise }: { who: string; text: ReactNode; time: string; rise?: boolean }) => {
  const label = who === 'cust' ? 'Customer' : who === 'ai' ? 'Imani (AI)' : 'You';
  return (
    <div className={`a-msg ${who}${who !== 'cust' ? ' right' : ''}${rise ? ' rise' : ''}`}>
      <span className="a-bubble">{text}</span>
      <small>{label} · {time}</small>
    </div>
  );
};

export const SellByChatView = ({
  shown = CHAT.length,
  typing = false,
  partial,
  taylorStatus = 'won',
  wonFlash = false,
  taylorPulse = false,
  showYou = true,
  riseFrom = Infinity,
}: {
  shown?: number;
  typing?: boolean;
  partial?: string;
  taylorStatus?: 'ai' | 'won';
  wonFlash?: boolean;
  taylorPulse?: boolean;
  showYou?: boolean;
  riseFrom?: number;
}) => {
  const last = partial ?? (shown > 0 ? CHAT[shown - 1].text : '');
  return (
    <>
      <p className="a-intro">Your live sales conversations. Watch them, and jump in whenever you want — BRANDCENTRAL is monitoring alongside you.</p>
      <div className="a-chat">
        <div className="a-card a-chat-list">
          {shown > 0 && (
            <div className={`a-row sel${taylorPulse ? ' pulse' : ''}`}>
              <div className="a-row-top">
                <b>Taylor Brooks</b>
                {taylorStatus === 'ai'
                  ? <span className="a-pill a-st-ai">AI</span>
                  : <span className={`a-pill a-st-won${wonFlash ? ' flash' : ''}`}>Closed · won</span>}
                <Funnel name="Catering" c={SKY} />
              </div>
              <p>{last}</p>
            </div>
          )}
          <div className="a-row">
            <div className="a-row-top"><b>Chris Ramos</b><span className="a-pill a-st-won">Closed · won</span><Funnel name="Wholesale" c={VIOLET} /></div>
            <p>Invoice received, thank you!</p>
          </div>
          <div className="a-row">
            <div className="a-row-top"><b>Dana Whitfield</b><span className="a-pill a-st-ai">AI</span><Funnel name="Catering" c={SKY} /></div>
            <p>Do you have oat milk options?</p>
          </div>
        </div>
        <div className="a-card a-thread">
          <div className="a-thread-head">
            <div><b>Taylor Brooks</b><span>SMS · AI handling · Catering funnel</span></div>
            <span className="a-btn-ghost">⚡ Pause AI / take over</span>
          </div>
          <div className="a-msgs">
            {CHAT.slice(0, shown).map((m, i) => (
              <Msg key={i} who={m.who} time={m.time} rise={i >= riseFrom}
                text={i === shown - 1 && partial !== undefined ? partial : m.text} />
            ))}
            {typing && (
              <div className="a-msg ai right"><span className="a-bubble a-typing"><i /><i /><i /></span></div>
            )}
            {showYou && <Msg who="you" text="Thanks Taylor! We'll have a few samples ready for the call." time="9:09" />}
          </div>
          <div className="a-composer"><div>Jump in…</div><span className="a-btn-primary">Send</span></div>
        </div>
      </div>
    </>
  );
};

// ── 4.4 Library & calendar ─────────────────────────────────────────────

const CalGlyph = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
);

export const CalendarRow = () => (
  <div className="a-cal-row">
    <span className="a-cal-tile"><CalGlyph /></span>
    <span><b>Content calendar</b><span className="meta">6 posts scheduled · next: Fall menu drop</span></span>
    <span className="a-pill a-pill-open">Open</span>
  </div>
);

type Chip = { label: string; kind: 'sched' | 'queued' | 'meet' };
const CAL_ITEMS: Record<number, Chip[]> = {
  16: [{ label: 'Fall menu drop', kind: 'sched' }],
  19: [{ label: 'Newsletter: fall menu', kind: 'sched' }],
  20: [{ label: '10:00 Catering call', kind: 'meet' }],
  21: [{ label: 'Behind the bar reel', kind: 'queued' }],
  23: [{ label: 'Cardamom bun launch', kind: 'sched' }],
  26: [{ label: 'Weekend recap', kind: 'sched' }],
  29: [{ label: 'Halloween latte art', kind: 'sched' }],
};

export const MonthCalendar = () => {
  // October 2026 starts on a Thursday; the grid starts Sunday, Sep 27.
  const cells = Array.from({ length: 35 }, (_, i) => {
    const d = i - 3;
    return d < 1 ? { n: 27 + i, out: true } : { n: d, out: false };
  });
  return (
    <div className="a-cal">
      <div className="a-cal-head">Content calendar <span>✕</span></div>
      <div className="a-cal-tools">
        <div className="a-cal-nav">‹ <span>Today</span> › <b>October 2026</b></div>
        <div className="a-seg"><span className="on">Month</span><span>Week</span></div>
      </div>
      <div className="a-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d} className="a-dow">{d}</div>)}
        {cells.map((c, i) => (
          <div key={i} className={`a-day${c.out ? ' out' : ''}`}>
            <span className={`a-dnum${!c.out && c.n === 14 ? ' today' : ''}`}>{c.n}</span>
            {!c.out && (CAL_ITEMS[c.n] ?? []).map((it) => <span key={it.label} className={`a-chip ${it.kind}`}>{it.label}</span>)}
          </div>
        ))}
      </div>
    </div>
  );
};

const LIB_ROWS = [
  { title: 'October newsletter: the fall menu is here', meta: 'Newsletter · Maya · approved Oct 14' },
  { title: 'Fall launch: 3 Instagram posts', meta: 'Instagram post · Diego · approved Oct 12' },
  { title: '90-day growth plan', meta: 'Plan · Rafael · approved Oct 3' },
];

export const LibraryView = ({ withNew = false }: { withNew?: boolean }) => (
  <div className="a-lib">
    <h3>Library</h3>
    <p>Everything your AI Staff produced that&rsquo;s been approved — plans, SOPs, proposals, campaigns and more — ready to read, copy or download.</p>
    <p className="a-caps">Agreements</p>
    <div className="a-card a-lib-row">
      <span><b>Service agreement</b><span>Signed · Sep 28</span></span>
      <span className="a-btn-ghost">Open PDF</span>
    </div>
    <p className="a-caps">October 2026</p>
    {withNew && (
      <div className="a-card a-lib-row new">
        <span><b>Fall menu drop</b><span>Instagram post · Diego · approved today</span></span>
        <em>Open</em>
      </div>
    )}
    {LIB_ROWS.map((r) => (
      <div key={r.title} className="a-card a-lib-row">
        <span><b>{r.title}</b><span>{r.meta}</span></span>
        <em>Open</em>
      </div>
    ))}
  </div>
);

export const LibraryCalendarView = () => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 560px', gap: 24, alignItems: 'start' }}>
    <LibraryView />
    <div>
      <CalendarRow />
      <MonthCalendar />
    </div>
  </div>
);
